// ============================================================================
// useAutoGenFilterConfigsFromTpl
// ============================================================================
// Thin React wrapper around ootils' autoGenFilterConfigsFromTpl. Its only job is
// to resolve the tenant-shaped arguments out of the react-query caches and hand
// them to the shared generator — the assembly itself lives in ootils so this
// sidebar, the AI Chat filter menu and the Filters Playground all read from one
// implementation instead of three copies that drift.
//
// Pipeline, options and output shape: ootils/src/utils/autoGenFilterConfigsFromTpl
import { useMemo, useState, useEffect } from "react";
import { autoGenFilterConfigsFromTpl, resolveTopicClusteringLens } from "@okf/ootils";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { DISABLED_REASONS } from "@/utils/disabledReasons";

// Re-exported so existing importers (FiltersPlayground) keep one source of truth
// for the split-mode Topics config, which now lives in ootils.
export { buildTopicClusteringFilterConfig } from "@okf/ootils";

/**
 * @param selectedTpls        templates to generate filters for
 * @param filterScopes        ["doc"] | ["anno"] | both
 * @param unifiedFilterGroup  flatten the section tree into a single config list
 * @param flatDocFilters      drop the dataset drilldown level on single-dataset pages
 * @param disableHierarchyUI  emit flat, individually-queryable configs instead of the
 *                            drilldown container configs — for surfaces that don't run
 *                            flattenFilterConfig (was: disableV3Hierarchy)
 * @param attributeThematicTabs  override WHICH sub-theme role tabs render (defaults to
 *                            ootils' ATTRIBUTE_THEMATIC_TABS). Does not change WHETHER
 *                            they render — that stays mode-driven.
 * @param enabledContentTypes dataset folders outside these are disabled with a hover
 *                            reason and listed last; omit to keep all enabled (stable reference)
 * @param excludeFilterIds    common filterIds to leave out (stable reference — it's a memo dep)
 * @param topicClusteringLens override the clustering-tree lens on the split-mode
 *                            Topics filter (default: the tenant's configured lens,
 *                            via ootils resolveTopicClusteringLens)
 */
export const useAutoGenFilterConfigsFromTpl = ({
  selectedTpls,
  filterScopes = ["doc", "anno"],
  enableAsyncLoading = false,
  enable = true,
  unifiedFilterGroup = false,
  flatDocFilters = false,
  disableHierarchyUI = false,
  attributeThematicTabs,
  topicClusteringLens: requestedLens,
  enabledContentTypes,
  excludeFilterIds,
}) => {
  const allTpls = useGetQueryData("allTpls");
  const platformConfigs = useGetQueryData("platformConfigs");
  const topicClusteringLens = resolveTopicClusteringLens({ platformConfigs_ai: platformConfigs?.ai, topicClusteringLens: requestedLens });
  const isSelfManagedTenant = platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.enable === true;
  const smTagTypesConfig = platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.annotation_tagTypesConfig || null;
  const annotationTagsCount = platformConfigs?.annotationTagsCount;
  const hasNewTopicModelling = getAnnoMode(platformConfigs) !== "legacy";
  // Split-mode-only filters (customerVoice, the clustering-tree Topics filter) are
  // gated to tenants running Claude C1 — the data only exists on annos from that pipeline.
  const isSplitMode = getAnnoMode(platformConfigs) === "split";

  const [loading, setLoading] = useState(enableAsyncLoading);

  if (enable === false) return undefined;

  const filterConfigs = useMemo(() => {
    if (!selectedTpls || !allTpls) return null;

    const result = autoGenFilterConfigsFromTpl({
      selectedTpls,
      allTpls,
      filterScopes,
      isSelfManagedTenant,
      annotationTagsCount,
      smTagTypesConfig,
      flatDocFilters,
      disableHierarchyUI,
      hasNewTopicModelling,
      isSplitMode,
      attributeThematicTabs,
      topicClusteringLens,
      enabledContentTypes,
      disabledDatasetReason: DISABLED_REASONS.NOT_IN_CONFIGURATION,
      excludeFilterIds,
    });

    if (unifiedFilterGroup && result) {
      const flattenedConfigs = [];

      result.forEach((section) => {
        if (section.configs && Array.isArray(section.configs)) {
          section.configs.forEach((config) => {
            if (config.configs && Array.isArray(config.configs)) {
              flattenedConfigs.push(...config.configs);
            } else {
              flattenedConfigs.push(config);
            }
          });
        }
      });

      return flattenedConfigs;
    }

    return result;
  }, [selectedTpls, allTpls, filterScopes, isSelfManagedTenant, annotationTagsCount, smTagTypesConfig, unifiedFilterGroup, flatDocFilters, disableHierarchyUI, hasNewTopicModelling, isSplitMode, attributeThematicTabs, topicClusteringLens, enabledContentTypes, excludeFilterIds]);

  useEffect(() => {
    if (enableAsyncLoading && filterConfigs !== null) {
      setLoading(false);
    }
  }, [enableAsyncLoading, filterConfigs]);

  if (enableAsyncLoading) {
    return {
      filterConfigs,
      loading,
      success: !loading && filterConfigs !== null,
    };
  }

  return filterConfigs;
};
